// src/pages/Domains.js
// ─────────────────────────────────────────────────────────────────────────
// Phase 5.2a — Domain Management page
//
// Five tabs (only "Hosting" is functional in 5.2a; rest are placeholders):
//   Hosting          — list/add/edit/disable/delete domains  ✅ this phase
//   Link Domains     — per-domain link aggregates             (later phase)
//   From Domains     — email-sending domain mgmt              (later phase)
//   Backups          — scheduled backups                      (later phase)
//   Tools            — DNS / blacklist / SSL utilities        (later phase)
//
// Write actions (Add / Edit / Disable / Re-enable / Permanent Delete /
// Test) require the `domains_manage` permission. Read-only access is
// gated by the `domains` permission via ProtectedRoute in App.js.
// ─────────────────────────────────────────────────────────────────────────

import { useState, useEffect, useCallback } from "react";
import Sidebar from "../components/Sidebar";
import Header  from "../components/Header";
import { useAuth } from "../context/AuthContext";
import axios from "axios";
import "../styles/domains.css";
import { edtDateTime } from "../utils/edt";

const API = process.env.REACT_APP_API_URL || "https://ipanel.idigitalhub.org/api";

const HOSTING_TYPES = [
  { value: "cpanel", label: "cPanel"  },
  { value: "plesk",  label: "Plesk"   },
  { value: "own_ip", label: "Own IP / VPS" },
];

/* ─── Add / Edit Domain Modal ─────────────── */
function DomainModal({ domain, onClose, onSave, onTest, onReveal, isAdmin }) {
  const isEdit = !!domain?.hostname;

  const [form, setForm] = useState({
    hostname:        domain?.hostname        || "",
    hosting_type:    domain?.hosting_type    || "cpanel",
    cpanel_user:     domain?.cpanel_user     || "",
    doc_root:        domain?.doc_root        || "",
    ssh_host:        domain?.ssh_host        || "",
    ssh_port:        domain?.ssh_port        || 22,
    ssh_user:        domain?.ssh_user        || "root",
    ssh_password:    "",
    notes:           domain?.notes           || "",
  });
  const [loading,    setLoading]    = useState(false);
  const [testing,    setTesting]    = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [error,      setError]      = useState("");
  const [fieldErrors, setFieldErrors] = useState({});

  // Reveal-password state
  const [revealing,      setRevealing]      = useState(false);
  const [revealedValue,  setRevealedValue]  = useState("");      // plaintext when revealed
  const [revealKind,     setRevealKind]     = useState(null);    // 'password' or 'private_key'
  const [revealSeconds,  setRevealSeconds]  = useState(0);       // countdown
  const [revealReason,   setRevealReason]   = useState("");
  const [revealError,    setRevealError]    = useState("");
  const [showReasonBox,  setShowReasonBox]  = useState(false);
  const [changeMode,     setChangeMode]     = useState(!isEdit); // false on edit by default; true on add

  // Auto-hide countdown — when revealedValue is set, count down from 30s
  useEffect(() => {
    if (!revealedValue) return;
    if (revealSeconds <= 0) {
      // expired — wipe
      setRevealedValue("");
      setRevealKind(null);
      return;
    }
    const t = setTimeout(() => setRevealSeconds(s => s - 1), 1000);
    return () => clearTimeout(t);
  }, [revealedValue, revealSeconds]);

  // Hide reveal when modal closes (safety)
  useEffect(() => {
    return () => {
      setRevealedValue("");
      setRevealKind(null);
      setRevealSeconds(0);
    };
  }, []);

  const handle = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true); setError(""); setFieldErrors({});
    try {
      await onSave(form, isEdit ? domain.hostname : null);
      onClose();
    } catch (err) {
      const data = err.response?.data;
      if (data?.fieldErrors) setFieldErrors(data.fieldErrors);
      setError(data?.message || "Error saving domain.");
    } finally { setLoading(false); }
  };

  const runTest = async () => {
    setTesting(true); setTestResult(null);
    try {
      const r = await onTest(form);
      setTestResult(r);
    } catch (err) {
      setTestResult({ ok: false, message: err.response?.data?.message || err.message });
    } finally { setTesting(false); }
  };

  const runReveal = async () => {
    if (!showReasonBox) { setShowReasonBox(true); return; }
    setRevealing(true); setRevealError("");
    try {
      const r = await onReveal(domain.hostname, revealReason);
      setRevealedValue(r.value);
      setRevealKind(r.kind);
      setRevealSeconds(30);                       // auto-hide in 30s
      setShowReasonBox(false);
    } catch (err) {
      setRevealError(err.response?.data?.message || err.message || "Failed to reveal credential.");
    } finally { setRevealing(false); }
  };

  const hideReveal = () => {
    setRevealedValue("");
    setRevealKind(null);
    setRevealSeconds(0);
    setRevealReason("");
  };

  return (
    <div className="dm-mo" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="dm-mo-box">
        <div className="dm-mo-hd">
          <span className="dm-mo-title">{isEdit ? "Edit Domain" : "Add New Domain"}</span>
          <button className="dm-mo-close" onClick={onClose}>✕</button>
        </div>

        <form onSubmit={submit} className="dm-mo-body">
          {/* Hostname */}
          <div className="dm-field">
            <label>Hostname *</label>
            <input
              type="text" required
              value={form.hostname}
              onChange={e => handle("hostname", e.target.value.toLowerCase().trim())}
              placeholder="example.com"
              disabled={isEdit}
            />
            {fieldErrors.hostname && <div className="dm-field-err">{fieldErrors.hostname}</div>}
            {isEdit && <div className="dm-field-hint">Hostname cannot be changed once created.</div>}
          </div>

          {/* Hosting type */}
          <div className="dm-field">
            <label>Hosting Type *</label>
            <div className="dm-seg">
              {HOSTING_TYPES.map(opt => (
                <label key={opt.value} className={`dm-seg-btn ${form.hosting_type === opt.value ? "active" : ""}`}>
                  <input
                    type="radio" name="hosting_type" value={opt.value}
                    checked={form.hosting_type === opt.value}
                    onChange={() => handle("hosting_type", opt.value)}
                  />
                  {opt.label}
                </label>
              ))}
            </div>
            {fieldErrors.hosting_type && <div className="dm-field-err">{fieldErrors.hosting_type}</div>}
          </div>

          {/* cPanel user (cpanel only) */}
          {form.hosting_type === "cpanel" && (
            <div className="dm-field">
              <label>cPanel User</label>
              <input
                type="text"
                value={form.cpanel_user}
                onChange={e => handle("cpanel_user", e.target.value)}
                placeholder="auto-derived from hostname if blank"
              />
            </div>
          )}

          {/* Doc root */}
          <div className="dm-field">
            <label>Doc Root</label>
            <input
              type="text"
              value={form.doc_root}
              onChange={e => handle("doc_root", e.target.value)}
              placeholder={
                form.hosting_type === "cpanel" ? "/home/<user>/public_html (auto)" :
                form.hosting_type === "plesk"  ? "/var/www/vhosts/<domain>/httpdocs (auto)" :
                                                  "/var/www/<domain>/public"
              }
            />
            <div className="dm-field-hint">Leave blank to use the default for the selected hosting type.</div>
          </div>

          {/* SSH connection details */}
          <div className="dm-field-grid">
            <div className="dm-field">
              <label>SSH Host</label>
              <input
                type="text" value={form.ssh_host}
                onChange={e => handle("ssh_host", e.target.value)}
                placeholder="Same as hostname if blank"
              />
            </div>
            <div className="dm-field">
              <label>SSH Port *</label>
              <input
                type="number" min="1" max="65535" required
                value={form.ssh_port}
                onChange={e => handle("ssh_port", parseInt(e.target.value) || 22)}
              />
              {fieldErrors.ssh_port && <div className="dm-field-err">{fieldErrors.ssh_port}</div>}
            </div>
            <div className="dm-field">
              <label>SSH User *</label>
              <input
                type="text" required
                value={form.ssh_user}
                onChange={e => handle("ssh_user", e.target.value)}
              />
              {fieldErrors.ssh_user && <div className="dm-field-err">{fieldErrors.ssh_user}</div>}
            </div>
          </div>

          {/* Password — smart edit/reveal/change UI */}
          <div className="dm-field">
            <label>
              SSH Password {!isEdit && "*"}
            </label>

            {/* === ADD MODE === plain password input === */}
            {!isEdit && (
              <input
                type="password"
                required
                value={form.ssh_password}
                onChange={e => handle("ssh_password", e.target.value)}
                placeholder="Enter SSH password"
                autoComplete="new-password"
              />
            )}

            {/* === EDIT MODE === three sub-states: locked, revealed, change === */}
            {isEdit && !changeMode && !revealedValue && (
              <div className="dm-pwd-row">
                <div className="dm-pwd-masked">••••••••••</div>
                <div className="dm-pwd-actions">
                  {isAdmin && (
                    <button type="button" className="dm-btn-reveal"
                      onClick={runReveal}
                      disabled={revealing}>
                      {revealing ? "..." : "👁 Reveal"}
                    </button>
                  )}
                  <button type="button" className="dm-btn-changepw"
                    onClick={() => setChangeMode(true)}>
                    ✎ Change Password
                  </button>
                </div>
              </div>
            )}

            {/* Reason box (admin only, shows before actual reveal) */}
            {isEdit && !changeMode && isAdmin && showReasonBox && !revealedValue && (
              <div className="dm-reveal-reason">
                <label className="dm-reveal-reason-lbl">Why are you revealing this? <span className="dm-muted">(optional, audited)</span></label>
                <input
                  type="text" autoFocus
                  value={revealReason}
                  onChange={e => setRevealReason(e.target.value)}
                  placeholder="e.g. Need to log in via terminal to fix cron"
                />
                <div className="dm-reveal-reason-btns">
                  <button type="button" className="dm-btn-cancel" onClick={() => { setShowReasonBox(false); setRevealReason(""); }}>Cancel</button>
                  <button type="button" className="dm-btn-reveal" onClick={runReveal} disabled={revealing}>
                    {revealing ? "Revealing..." : "👁 Reveal Now"}
                  </button>
                </div>
              </div>
            )}

            {/* Revealed value with countdown */}
            {isEdit && !changeMode && revealedValue && (
              <div className="dm-revealed">
                <div className="dm-revealed-head">
                  <span className="dm-revealed-lbl">
                    {revealKind === 'private_key' ? '🔑 Private Key' : '🔓 Password'}
                    <span className="dm-revealed-warn"> · visible — auto-hides in {revealSeconds}s</span>
                  </span>
                  <button type="button" className="dm-btn-hide" onClick={hideReveal}>Hide</button>
                </div>
                {revealKind === 'private_key' ? (
                  <textarea
                    className="dm-revealed-value"
                    readOnly
                    value={revealedValue}
                    rows={6}
                    onFocus={e => e.target.select()}
                  />
                ) : (
                  <input
                    type="text" readOnly
                    className="dm-revealed-value"
                    value={revealedValue}
                    onFocus={e => e.target.select()}
                  />
                )}
              </div>
            )}

            {/* Change-password sub-form */}
            {isEdit && changeMode && (
              <div className="dm-pwd-change">
                <input
                  type="password"
                  value={form.ssh_password}
                  onChange={e => handle("ssh_password", e.target.value)}
                  placeholder="Enter new SSH password"
                  autoComplete="new-password"
                  autoFocus
                />
                <button type="button" className="dm-btn-cancel-change"
                  onClick={() => { setChangeMode(false); handle("ssh_password", ""); }}>
                  Cancel change
                </button>
              </div>
            )}

            {revealError && <div className="dm-field-err">{revealError}</div>}
            {fieldErrors.ssh_password && <div className="dm-field-err">{fieldErrors.ssh_password}</div>}
          </div>

          {/* Notes */}
          <div className="dm-field">
            <label>Notes</label>
            <textarea
              value={form.notes}
              onChange={e => handle("notes", e.target.value)}
              placeholder="Optional notes about this domain"
              rows={2}
            />
          </div>

          {/* Test connection result */}
          {testResult && (
            <div className={`dm-test-result ${testResult.ok ? "ok" : "fail"}`}>
              {testResult.ok ? "✅ " : "❌ "}
              {testResult.message}
              {typeof testResult.durationMs === "number" && (
                <span className="dm-test-time"> ({testResult.durationMs}ms)</span>
              )}
            </div>
          )}

          {error && <div className="dm-mo-err">⚠ {error}</div>}

          <div className="dm-mo-foot">
            <button
              type="button"
              className="dm-btn-test"
              onClick={runTest}
              disabled={testing || !form.hostname || (!form.ssh_password && !isEdit)}
            >
              {testing ? "Testing..." : "🔌 Test Connection"}
            </button>
            <div className="dm-mo-foot-r">
              <button type="button" className="dm-btn-cancel" onClick={onClose}>Cancel</button>
              <button type="submit" className="dm-btn-save" disabled={loading}>
                {loading ? "Saving..." : isEdit ? "Update" : "Add Domain"}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}

/* ─── Disable Confirmation Modal ──────────── */
function DisableModal({ domain, onClose, onConfirm }) {
  const [loading, setLoading] = useState(false);
  const [reason, setReason]   = useState("");
  return (
    <div className="dm-mo" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="dm-mo-box" style={{ maxWidth: 440 }}>
        <div className="dm-mo-hd">
          <span className="dm-mo-title" style={{ color: "#D97706" }}>Disable Domain</span>
          <button className="dm-mo-close" onClick={onClose}>✕</button>
        </div>
        <div className="dm-mo-body">
          <p style={{ marginBottom: 12 }}>
            Disable <strong>{domain.hostname}</strong>?
          </p>
          <p style={{ color: "var(--text-secondary)", fontSize: 13, marginBottom: 12 }}>
            New tracking-link deployments to this domain will be blocked. Existing deployed
            redirects keep working. Re-enable any time.
          </p>
          <div className="dm-field">
            <label>Reason (optional)</label>
            <input
              type="text" value={reason}
              onChange={e => setReason(e.target.value)}
              placeholder="e.g. SSL expired, hosting moving"
            />
          </div>
          <div className="dm-mo-foot" style={{ marginTop: 16 }}>
            <div style={{ flex: 1 }} />
            <div className="dm-mo-foot-r">
              <button className="dm-btn-cancel" onClick={onClose}>Cancel</button>
              <button
                className="dm-btn-disable" disabled={loading}
                onClick={async () => {
                  setLoading(true);
                  try { await onConfirm(domain.hostname, reason); onClose(); }
                  finally { setLoading(false); }
                }}>
                {loading ? "Disabling..." : "Disable"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─── Permanent Delete Modal ──────────────── */
function PermanentDeleteModal({ domain, onClose, onConfirm }) {
  const [loading, setLoading] = useState(false);
  const [typed,   setTyped]   = useState("");
  const [err,     setErr]     = useState("");
  const valid = typed.trim().toLowerCase() === domain.hostname;

  return (
    <div className="dm-mo" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="dm-mo-box" style={{ maxWidth: 440 }}>
        <div className="dm-mo-hd">
          <span className="dm-mo-title" style={{ color: "#DC2626" }}>Permanently Delete Domain</span>
          <button className="dm-mo-close" onClick={onClose}>✕</button>
        </div>
        <div className="dm-mo-body">
          <p style={{ marginBottom: 10, color: "#DC2626", fontWeight: 600 }}>
            ⚠ This cannot be undone.
          </p>
          <p style={{ color: "var(--text-secondary)", fontSize: 13, marginBottom: 14 }}>
            The domain row and its stored credentials will be removed.
            Cannot proceed if any tracking links still reference this domain.
          </p>
          <div className="dm-field">
            <label>Type <code style={{ background: "var(--bg-card2)", padding: "1px 6px", borderRadius: 3 }}>{domain.hostname}</code> to confirm</label>
            <input
              type="text" value={typed}
              onChange={e => { setTyped(e.target.value); setErr(""); }}
              placeholder={domain.hostname}
              autoFocus
            />
          </div>
          {err && <div className="dm-mo-err">⚠ {err}</div>}
          <div className="dm-mo-foot" style={{ marginTop: 16 }}>
            <div style={{ flex: 1 }} />
            <div className="dm-mo-foot-r">
              <button className="dm-btn-cancel" onClick={onClose}>Cancel</button>
              <button
                className="dm-btn-delete"
                disabled={loading || !valid}
                onClick={async () => {
                  setLoading(true); setErr("");
                  try { await onConfirm(domain.hostname); onClose(); }
                  catch (e) { setErr(e.response?.data?.message || e.message); }
                  finally { setLoading(false); }
                }}>
                {loading ? "Deleting..." : "Permanently Delete"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─── Status Badge ────────────────────────── */
function StatusBadge({ status }) {
  return (
    <span className={`dm-status dm-status-${status}`}>
      {status === "active" ? "● Active" : "● Disabled"}
    </span>
  );
}

/* ─── Health Badge ────────────────────────── */
function HealthBadge({ row }) {
  const s = row.last_connection_status || "untested";
  const when = row.last_connection_test_at
    ? edtDateTime(row.last_connection_test_at) + " EDT"
    : "Never tested";
  return (
    <span className={`dm-health dm-health-${s}`} title={
      s === "ok"   ? `Last ok: ${when}`
    : s === "fail" ? `Last error: ${row.last_connection_error || "(no message)"}\n${when}`
    :                "No test recorded"
    }>
      {s === "ok" ? "🟢" : s === "fail" ? "🔴" : "⚪"}
      <span className="dm-health-lbl">
        {s === "ok" ? "Healthy" : s === "fail" ? "Error" : "Untested"}
      </span>
    </span>
  );
}

/* ─── Placeholder tab content for upcoming phases ─── */
function ComingSoonTab({ title, phase, blurb }) {
  return (
    <div className="dm-coming">
      <div className="dm-coming-icon">🚧</div>
      <div className="dm-coming-title">{title}</div>
      <div className="dm-coming-phase">Coming in Phase {phase}</div>
      <p className="dm-coming-blurb">{blurb}</p>
    </div>
  );
}

/* ─── Hosting Tab — the working content of 5.2a ─── */
function HostingTab({ canManage, isAdmin }) {
  const [domains, setDomains] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search,  setSearch]  = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [typeFilter,   setTypeFilter]   = useState("all");

  const [addModal,  setAddModal]    = useState(false);
  const [editRow,   setEditRow]     = useState(null);
  const [disableRow,setDisableRow]  = useState(null);
  const [deleteRow, setDeleteRow]   = useState(null);
  const [testing,   setTesting]     = useState(null);   // hostname being tested
  const [toast,     setToast]       = useState("");
  const [error,     setError]       = useState("");

  const showToast = (m) => { setToast(m); setTimeout(() => setToast(""), 3500); };
  const showError = (m) => { setError(m); setTimeout(() => setError(""), 6000); };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/domains`);
      setDomains(r.data.domains || []);
    } catch (e) {
      showError(e.response?.data?.message || "Failed to load domains.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  /* ── Filtered list ── */
  const filtered = domains.filter(d => {
    if (statusFilter !== "all" && d.status !== statusFilter) return false;
    if (typeFilter   !== "all" && d.hosting_type !== typeFilter) return false;
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      if (!d.hostname.toLowerCase().includes(q) &&
          !(d.cpanel_user || "").toLowerCase().includes(q) &&
          !(d.notes || "").toLowerCase().includes(q)) return false;
    }
    return true;
  });

  /* ── Save (create OR update) ── */
  const handleSave = async (form, existingHostname) => {
    const body = { ...form };
    if (!body.ssh_password) delete body.ssh_password;
    if (existingHostname) {
      delete body.hostname;   // hostname is immutable
      await axios.put(`${API}/domains/${existingHostname}`, body);
      showToast(`${existingHostname} updated.`);
    } else {
      await axios.post(`${API}/domains`, body);
      showToast(`${form.hostname} added.`);
    }
    load();
  };

  /* ── Test (ad-hoc, from modal) ── */
  const handleTestAdHoc = async (form) => {
    const r = await axios.post(`${API}/domains/test`, {
      hostname:     form.hostname,
      ssh_host:     form.ssh_host || null,
      ssh_port:     form.ssh_port,
      ssh_user:     form.ssh_user,
      ssh_password: form.ssh_password || null,
    });
    return r.data;
  };

  /* ── Reveal stored password (admin-only; audited) ── */
  const handleReveal = async (hostname, reason) => {
    const r = await axios.post(`${API}/domains/${hostname}/reveal`, { reason: reason || null });
    return r.data;   // { value, kind, revealed_at, ... }
  };

  /* ── Test (stored creds, from row action) ── */
  const handleTestStored = async (hostname) => {
    setTesting(hostname);
    try {
      const r = await axios.post(`${API}/domains/${hostname}/test`);
      showToast(r.data.ok
        ? `${hostname}: connected ✓ (${r.data.durationMs}ms)`
        : `${hostname}: ${r.data.message}`);
      load();
    } catch (e) {
      showError(e.response?.data?.message || e.message);
    } finally {
      setTesting(null);
    }
  };

  /* ── Disable ── */
  const handleDisable = async (hostname, reason) => {
    await axios.put(`${API}/domains/${hostname}`, { status: "disabled", disabled_reason: reason || null });
    showToast(`${hostname} disabled.`);
    load();
  };

  /* ── Re-enable ── */
  const handleEnable = async (hostname) => {
    await axios.put(`${API}/domains/${hostname}`, { status: "active" });
    showToast(`${hostname} re-enabled.`);
    load();
  };

  /* ── Permanent delete ── */
  const handleDeletePermanent = async (hostname) => {
    await axios.delete(`${API}/domains/${hostname}/permanent`, {
      data: { confirm: hostname },
    });
    showToast(`${hostname} permanently deleted.`);
    load();
  };

  const counts = {
    total:    domains.length,
    active:   domains.filter(d => d.status === "active").length,
    disabled: domains.filter(d => d.status === "disabled").length,
  };

  return (
    <>
      {/* Toolbar */}
      <div className="dm-toolbar">
        <div className="dm-toolbar-l">
          <input
            className="dm-search" type="text"
            placeholder="Search hostname, cpanel user, notes..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          <select className="dm-select" value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
            <option value="all">All statuses</option>
            <option value="active">Active</option>
            <option value="disabled">Disabled</option>
          </select>
          <select className="dm-select" value={typeFilter} onChange={e => setTypeFilter(e.target.value)}>
            <option value="all">All hosting types</option>
            {HOSTING_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
        </div>
        <div className="dm-toolbar-r">
          <span className="dm-counts">
            <strong>{counts.total}</strong> total &nbsp;·&nbsp;
            <span className="dm-c-active">{counts.active} active</span> &nbsp;·&nbsp;
            <span className="dm-c-disabled">{counts.disabled} disabled</span>
          </span>
          {canManage && (
            <button className="dm-btn-add" onClick={() => setAddModal(true)}>
              + Add Domain
            </button>
          )}
        </div>
      </div>

      {error && <div className="dm-error-banner">{error}</div>}

      {/* Table */}
      <div className="dm-table-wrap">
        <div className="dm-table-scroll">
          {loading ? (
            <div className="dm-loading">Loading domains...</div>
          ) : filtered.length === 0 ? (
            <div className="dm-empty">
              <div style={{ fontSize: 40, marginBottom: 8 }}>🌐</div>
              {domains.length === 0
                ? "No domains registered yet."
                : "No domains match the current filters."}
              {domains.length === 0 && canManage && (
                <button className="dm-btn-add" onClick={() => setAddModal(true)} style={{ marginTop: 14 }}>
                  + Add Your First Domain
                </button>
              )}
            </div>
          ) : (
            <table className="dm-table">
              <thead>
                <tr>
                  <th>Hostname</th>
                  <th>Hosting</th>
                  <th>Health</th>
                  <th>Status</th>
                  <th>Links</th>
                  <th>Credentials</th>
                  <th>Last Test</th>
                  <th className="dm-th-actions">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(d => (
                  <tr key={d.hostname}>
                    <td>
                      <div className="dm-host">{d.hostname}</div>
                      {d.notes && <div className="dm-host-note" title={d.notes}>{d.notes}</div>}
                    </td>
                    <td>
                      <span className="dm-type-chip">{HOSTING_TYPES.find(t => t.value === d.hosting_type)?.label || d.hosting_type}</span>
                      {d.cpanel_user && <div className="dm-cpu">{d.cpanel_user}</div>}
                    </td>
                    <td><HealthBadge row={d}/></td>
                    <td><StatusBadge status={d.status}/></td>
                    <td>
                      <div className="dm-links-cell">
                        <span title="Active">{d.links_active || 0}</span>
                        <span className="dm-sep">/</span>
                        <span title="Total" className="dm-links-total">{d.links_total || 0}</span>
                      </div>
                    </td>
                    <td>
                      {d.has_private_key
                        ? <span className="dm-cred-chip key">🔑 Key</span>
                        : d.has_password
                          ? <span className="dm-cred-chip pw">🔒 Password</span>
                          : <span className="dm-cred-chip none">⚠ None</span>}
                    </td>
                    <td className="dm-cell-sm">
                      {d.last_connection_test_at
                        ? edtDateTime(d.last_connection_test_at)
                        : <span className="dm-muted">Never</span>}
                    </td>
                    <td className="dm-actions">
                      {canManage && (
                        <>
                          <button
                            className="dm-act dm-act-test"
                            disabled={testing === d.hostname}
                            onClick={() => handleTestStored(d.hostname)}
                            title="Test SSH connection">
                            {testing === d.hostname ? "..." : "🔌"}
                          </button>
                          <button
                            className="dm-act dm-act-edit"
                            onClick={() => setEditRow(d)}
                            title="Edit">✏</button>
                          {d.status === "active" ? (
                            <button
                              className="dm-act dm-act-disable"
                              onClick={() => setDisableRow(d)}
                              title="Disable">⏸</button>
                          ) : (
                            <button
                              className="dm-act dm-act-enable"
                              onClick={() => handleEnable(d.hostname)}
                              title="Re-enable">▶</button>
                          )}
                          <button
                            className="dm-act dm-act-del"
                            onClick={() => setDeleteRow(d)}
                            title="Permanently delete">🗑</button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Modals */}
      {addModal && (
        <DomainModal
          domain={null}
          onClose={() => setAddModal(false)}
          onSave={handleSave}
          onTest={handleTestAdHoc}
          onReveal={handleReveal}
          isAdmin={isAdmin}
        />
      )}
      {editRow && (
        <DomainModal
          domain={editRow}
          onClose={() => setEditRow(null)}
          onSave={handleSave}
          onTest={handleTestAdHoc}
          onReveal={handleReveal}
          isAdmin={isAdmin}
        />
      )}
      {disableRow && (
        <DisableModal
          domain={disableRow}
          onClose={() => setDisableRow(null)}
          onConfirm={handleDisable}
        />
      )}
      {deleteRow && (
        <PermanentDeleteModal
          domain={deleteRow}
          onClose={() => setDeleteRow(null)}
          onConfirm={handleDeletePermanent}
        />
      )}
      {toast && <div className="dm-toast">{toast}</div>}
    </>
  );
}

/* ─── Main Page ───────────────────────────── */
export default function Domains() {
  const { user, can } = useAuth();
  const [tab, setTab] = useState("hosting");

  const canManage = can("domains_manage") || user?.role === "admin";
  const isAdmin   = user?.role === "admin";

  return (
    <div className="layout">
      <Sidebar active="domains" />
      <div className="layout-main">
        <Header title="Domain Management" userName={user?.name} />
        <div className="content dm-content">
          {/* Tab strip */}
          <div className="dm-tabs">
            <button className={`dm-tab ${tab === "hosting"      ? "active" : ""}`} onClick={() => setTab("hosting")}>Hosting</button>
            <button className={`dm-tab ${tab === "link-domains" ? "active" : ""}`} onClick={() => setTab("link-domains")}>Link Domains</button>
            <button className={`dm-tab ${tab === "from-domains" ? "active" : ""}`} onClick={() => setTab("from-domains")}>From Domains</button>
            <button className={`dm-tab ${tab === "backups"      ? "active" : ""}`} onClick={() => setTab("backups")}>Backups</button>
            <button className={`dm-tab ${tab === "tools"        ? "active" : ""}`} onClick={() => setTab("tools")}>Tools</button>
          </div>

          {/* Tab content */}
          <div className="dm-tab-panel">
            {tab === "hosting" && <HostingTab canManage={canManage} isAdmin={isAdmin} />}
            {tab === "link-domains" && (
              <ComingSoonTab
                title="Manage Link Domains"
                phase="5.2 (later)"
                blurb="Per-domain view of all tracking links — aggregate click counts, bulk disable/enable, redeploy across a domain."
              />
            )}
            {tab === "from-domains" && (
              <ComingSoonTab
                title="From Email Domains"
                phase="5.2e"
                blurb="Register email-sending domains. SPF / DKIM / DMARC validation. Assignment to ESP accounts."
              />
            )}
            {tab === "backups" && (
              <ComingSoonTab
                title="Domain Backups"
                phase="5.2f"
                blurb="Scheduled and on-demand backups of hosting files and configs to /var/backups/ipanel/."
              />
            )}
            {tab === "tools" && (
              <ComingSoonTab
                title="Domain Tools"
                phase="5.2g"
                blurb="DNS lookup, MX records, Spamhaus / SURBL blacklist check, SSL certificate expiry, port scanner."
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
